import { useEffect, useReducer, useRef, useState, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import * as api from "../api/client";
import { ApiError } from "../api/types";
import { currentTurn, initialState, nextId, reducer } from "./reducer";
import { clearSession, loadSession, saveSession } from "./persistence";
import {
  registerSession,
  track,
  trackFlowEnded,
  trackFlowStarted,
  trackQuestionAnswered,
  trackQuestionViewed,
  type InputKind,
} from "../analytics/posthog";
import type { Turn } from "../api/types";

/** Analytics per bot turn — metadata only (node ids / flags, never content). */
function trackBotTurn(turn: Turn): void {
  if (turn.terminal || turn.crisis) {
    trackFlowEnded({ crisis: turn.crisis, service: turn.service_recommended });
    return;
  }
  trackQuestionViewed(turn.node_id, turn.node_type);
}

const RETRY = (count: number, err: unknown) => count < 2 && api.isRetryable(err);
const RETRY_DELAY = (attempt: number) => Math.min(1000 * 2 ** attempt, 4000);

/** Remembered across refreshes so a resident who chose Punjabi doesn't have to
 *  choose again. Not PII — a UI preference only. */
const LANG_KEY = "wire_lang_v1";
const readLang = (): string => {
  try {
    return localStorage.getItem(LANG_KEY) || "en";
  } catch {
    return "en";
  }
};

/** Where the resident came from (`?src=qr-llr` appended by the QR edge
 *  redirect). Captured once into sessionStorage so it survives the in-app
 *  navigation between landing and the conversation actually starting, but
 *  dies with the tab — a shared library computer must not tag the NEXT
 *  person's session with the previous scan. Not PII: a printed-material slug,
 *  validated to a short token before it is ever sent. */
const SRC_KEY = "wire_entry_src_v1";
const readSrc = (): string | undefined => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("src");
    if (fromUrl && /^[A-Za-z0-9_-]{1,32}$/.test(fromUrl)) {
      sessionStorage.setItem(SRC_KEY, fromUrl.toLowerCase());
    }
    return sessionStorage.getItem(SRC_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

export function useConversation() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const initialised = useRef(false);
  const [lang, setLang] = useState<string>(readLang);
  const [languages, setLanguages] = useState<string[]>(["en"]);
  const [languageBusy, setLanguageBusy] = useState(false);

  // What this deployment can actually serve. Failure degrades to English-only,
  // which just hides the switcher — it never blocks the conversation.
  useEffect(() => {
    let cancelled = false;
    void api.fetchLanguages().then((l) => {
      if (!cancelled) setLanguages(l);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  // The last user message actually sent to the server, kept so a failed send
  // can be retried verbatim.
  const lastSent = useRef<string | null>(null);

  const startMutation = useMutation({
    mutationFn: () => api.startConversation(undefined, readLang(), readSrc()),
    retry: RETRY,
    retryDelay: RETRY_DELAY,
  });
  const sendMutation = useMutation({
    mutationFn: (message: string) => {
      if (!state.sessionId) throw new ApiError("No active session", null);
      return api.sendMessage(state.sessionId, message);
    },
    retry: RETRY,
    retryDelay: RETRY_DELAY,
  });

  const start = useCallback(async () => {
    dispatch({ type: "START" });
    try {
      const turn = await startMutation.mutateAsync();
      dispatch({ type: "SESSION_STARTED", turn });
      registerSession(turn.session_id);
      trackFlowStarted();
      trackBotTurn(turn);
    } catch {
      dispatch({
        type: "ERROR",
        message: "We couldn't start the conversation. Please check your connection and try again.",
      });
    }
  }, [startMutation]);

  // Restore a cached session on refresh, otherwise start fresh. Runs once.
  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    const cached = loadSession();
    if (cached) {
      dispatch({ type: "RESTORE", sessionId: cached.sessionId, log: cached.log });
      // The cached transcript deliberately carries no `collected_data` (the
      // structured PII payload is never at rest in the browser), so fetch the
      // answers back from the server — otherwise the referral form opens empty
      // after a refresh. Best-effort: the conversation still works without it.
      void api.getConversation(cached.sessionId)
        .then((turn) => dispatch({ type: "COLLECTED_RESTORED", turn }))
        .catch(() => { /* stale/expired session — the transcript still renders */ });
      registerSession(cached.sessionId);
      track("session_restored", {});
      // Re-arm question tracking for the turn back on screen, else the first
      // question_answered after a reload is silently dropped. (Timing restarts
      // from the restore — honest: we can't know time spent before it.)
      for (let i = cached.log.length - 1; i >= 0; i--) {
        const entry = cached.log[i];
        if (entry.role === "bot") {
          if (!entry.turn.terminal && !entry.turn.crisis) {
            trackQuestionViewed(entry.turn.node_id, entry.turn.node_type);
          }
          break;
        }
      }
    } else {
      void start();
    }
  }, [start]);

  const turn = currentTurn(state);

  // Persist (PII-stripped) whenever the transcript advances. Once a terminal
  // turn is reached (completion / crisis exit / signpost), clear the cache
  // instead — so a finished conversation can't linger on a shared device and be
  // restored by the next person (IG / DPIA: shared-device privacy).
  useEffect(() => {
    if (!state.sessionId || state.log.length === 0) return;
    if (turn?.terminal || turn?.crisis) {
      clearSession();
    } else {
      saveSession(state.sessionId, state.log);
    }
  }, [state.sessionId, state.log, turn]);

  /**
   * Send the resident's answer. `message` is the value sent to the engine
   * (option value or typed text); `displayText` is what's shown in the bubble.
   */
  const send = useCallback(
    async (
      message: string,
      displayText?: string,
      inputKind: InputKind = "option",
      { echo = true }: { echo?: boolean } = {},
    ) => {
      const trimmed = message.trim();
      if (!trimmed || state.status === "sending" || state.status === "starting") return;
      lastSent.current = trimmed;
      // `echo: false` for the opening acknowledgement: clicking "Got it, I'm
      // ready to begin" is not something the resident said, so echoing it into
      // the transcript reads as a reply they never made (Yvett, 18 Aug).
      if (echo) {
        dispatch({ type: "USER_SENT", id: nextId(), text: (displayText ?? message).trim() });
      }
      trackQuestionAnswered(inputKind);
      try {
        const turn = await sendMutation.mutateAsync(trimmed);
        dispatch({ type: "BOT_REPLY", turn });
        trackBotTurn(turn);
      } catch (err) {
        // Stale session on the server — clear cache and start over cleanly.
        if (err instanceof ApiError && err.status === 404) {
          clearSession();
          await start();
          return;
        }
        track("send_failed", {});
        dispatch({
          type: "ERROR",
          message: "Something went wrong sending that. You can try again.",
        });
      }
    },
    [sendMutation, start, state.status],
  );

  /** Re-send the last message after a failure. */
  const retry = useCallback(async () => {
    if (lastSent.current == null) {
      await start();
      return;
    }
    const msg = lastSent.current;
    track("send_retried", {});
    try {
      const turn = await sendMutation.mutateAsync(msg);
      dispatch({ type: "BOT_REPLY", turn });
      trackBotTurn(turn);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        clearSession();
        await start();
        return;
      }
      dispatch({ type: "ERROR", message: "Still couldn't send. Please try again shortly." });
    }
  }, [sendMutation, start]);

  /** Start a brand-new conversation (settings → "start fresh"). */
  const reset = useCallback(async () => {
    clearSession();
    lastSent.current = null;
    dispatch({ type: "RESET" });
    await start();
  }, [start]);

  /**
   * Switch language mid-conversation. Forward-only: the server re-renders the
   * current question in the new language and earlier turns stay as they were,
   * so the transcript honestly shows what the resident was actually asked.
   */
  const changeLanguage = useCallback(
    async (next: string) => {
      setLang(next);
      try {
        localStorage.setItem(LANG_KEY, next);
      } catch {
        /* private mode — the choice just won't persist */
      }
      if (!state.sessionId) return; // not started yet; start() will use it
      setLanguageBusy(true);
      try {
        const turn = await api.setLanguage(state.sessionId, next);
        dispatch({ type: "RETRANSLATED", turn });
      } catch {
        /* keep the conversation usable if the switch call fails */
      } finally {
        setLanguageBusy(false);
      }
    },
    [state.sessionId],
  );

  return {
    state,
    currentTurn: turn,
    send,
    retry,
    reset,
    lang,
    languages,
    languageBusy,
    changeLanguage,
  };
}
